import { runsOnDevice } from "../api/client.js";
import { providerBody } from "../epg/api.js";
import type { MediaFormat } from "@hybrid-iptv/types";

/** A same-origin stream URL plus whether it is a live-shaped transcode pipe. */
export interface ResolvedStream {
  url: string;
  /** True when the server transcodes this stream (live-shaped, no ranges). */
  transcoded: boolean;
}

/**
 * Resolve a provider stream URL to a same-origin URL the Section 5 player can
 * load. Same-origin URLs pass through; anything else is exchanged for a
 * short-lived token via POST /api/stream/token (the proxy rewrites manifests
 * and forwards bytes, so the browser never talks to the provider directly).
 *
 * The token response also says whether the proxy will transcode the stream
 * (FFmpeg pipe, live-shaped); the player needs that to pick the right
 * mpegts.js config instead of assuming every VOD is finite.
 */
export async function resolveStreamUrl(raw: string, media?: MediaFormat): Promise<ResolvedStream> {
  // The default: tokenize with the ACTIVE provider's credentials, which is
  // right for any URL the current catalog produced.
  return resolveStreamUrlWithProvider(raw, providerBody(), media);
}

/**
 * Tokenize a provider stream URL against an EXPLICIT provider credentials body
 * (default fallback: the active provider). Cross-provider mirrors (Section 16)
 * pass the mirror target's OWN providerBody so the token is minted against the
 * account that actually hosts the stream — a fallback URL tokenized with the
 * active provider's creds would be broken or, worse, a raw connection.
 */
export async function resolveStreamUrlWithProvider(
  raw: string,
  creds: Record<string, unknown>,
  media?: MediaFormat,
): Promise<ResolvedStream> {
  // Inside the APK the native player plays straight from the provider (Section
  // 22 Phase 1): there is no server to mint a token or hydrate the URL, so the
  // catalog's redacted URL gets the viewer's credentials back here, on the
  // device, and goes to ExoPlayer as it is. Loaded only in the APK.
  if (runsOnDevice()) {
    const { hydrateRedactedUrl, providerCredentials } = await import("@hybrid-iptv/iptv-parser");
    const { username, password } = providerCredentials(creds);
    return { url: hydrateRedactedUrl(raw, username, password), transcoded: false };
  }
  const parsed = new URL(raw, window.location.href);
  if (parsed.origin === window.location.origin) {
    return { url: parsed.toString(), transcoded: false };
  }
  // Send the supplied provider credentials so the API hydrates the redacted
  // stream URL with the right account rather than the server's env credentials.
  // Without them a bring-your-own-provider install can browse the catalog but
  // never play anything: the URL reaches the proxy still containing
  // `[redacted]`, which every provider rejects.
  const response = await fetch("/api/stream/token", {
    method: "POST",
    headers: { "content-type": "application/json" },
    // The provider's measurement of the file, when there is one: the server then
    // knows whether the sound needs converting without probing the file first,
    // which cost a provider connection and seconds before the picture (2026-10-09).
    body: JSON.stringify({ url: parsed.toString(), ...creds, ...(media?.videoCodec && media.audioCodec ? { codecs: { video: media.videoCodec, audio: media.audioCodec } } : {}) }),
  });
  if (!response.ok) {
    // Any public provider host is accepted (owner 2026-09-17); a refusal now
    // means the URL points at a private/loopback address, or is not HTTP(S).
    throw new Error("This stream address was refused: it must be a public http(s) address.");
  }
  const body = (await response.json()) as { token?: string; transcoded?: boolean };
  if (!body.token) throw new Error("The API did not issue a stream token.");
  return {
    url: `${window.location.origin}/api/stream?token=${encodeURIComponent(body.token)}`,
    transcoded: body.transcoded === true,
  };
}
